import { useState, type ReactNode } from 'react'

const COLLAPSE_LINES = 10
const COLLAPSE_CHARS = 800
const COLLAPSED_MAX_HEIGHT = 168

/**
 * A code block that collapses when it's long, with a Show more / Show less
 * toggle. `text` is the raw content (used to decide length + as the fallback
 * body); `children` lets callers supply already-rendered nodes (HTML path).
 */
export function CollapsibleCode({ text, children }: { text: string; children?: ReactNode }) {
  const trimmed = text.replace(/^\n+|\n+$/g, '')
  const lines = trimmed ? trimmed.split('\n').length : 0
  const long = lines > COLLAPSE_LINES || trimmed.length > COLLAPSE_CHARS
  const [expanded, setExpanded] = useState(false)
  const collapsed = long && !expanded

  return (
    <div className="my-1 rounded-md bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] overflow-hidden">
      {long && (
        <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-data text-[rgb(var(--np-tx-muted))] border-b border-[rgb(var(--np-border))]">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
          </svg>
          {lines} lines
        </div>
      )}
      <pre
        className="px-3 py-2 text-xs font-data overflow-x-auto whitespace-pre-wrap break-words"
        style={collapsed ? { maxHeight: COLLAPSED_MAX_HEIGHT, overflowY: 'hidden' } : undefined}
      >
        {children ?? trimmed}
      </pre>
      {long && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-3 py-1 text-left text-[11px] font-medium text-[rgb(var(--np-accent))] hover:bg-[rgb(var(--np-overlay))] border-t border-[rgb(var(--np-border))]"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}
